import React, { useMemo } from "react";
import {
  ArrowDownTrayIcon,
  ArrowUpRightIcon,
  CalendarIcon,
  ClipboardDocumentCheckIcon,
  HandRaisedIcon,
  IdentificationIcon,
  SparklesIcon,
  TagIcon,
  TrashIcon,
  UserMinusIcon,
} from "@heroicons/react/24/outline";
import { Group, Merge, Route } from "lucide-react";
import {
  DispatchableType,
  RoutingJobStatus,
  ShipmentStatus,
} from "@api/graphql/generated/generated-types";
import { useModalsContext } from "../context/ShipmentsTableModalsContext";
import { OrdersPageModalNames } from "../utils/ordersPageModalNames";
import { useShipmentFiltersContext } from "../context/ShipmentFiltersContext";
import { ShipmentsPrimaryFilterType } from "../types";
import { useShipmentsCoreDataContext } from "../context/ShipmentsCoreData";
import { useShipmentSelectionContext } from "../context/ShipmentSelectionContext";
import useBulkChangeDrivers from "./useBulkChangeDrivers";
import { useBulkPrintBOLs } from "./useBulkPrintBOLs";
import { getOptimizeRoutesState } from "../utils/optimizeRoutesEligibility";
import { useAuth } from "@src/common/hooks/useAuth";
import { useDispatchDelayCountdown } from "@src/common/components/Widgets/DispatchCandidatesWidget/hooks/useDispatchDelayCountdown";

export type BulkActionButtonColor = "purple" | "secondary" | "red";

const NON_DISPATCHABLE_SELECTED_REASON =
  "Your selection includes a billing-only order, which can't be dispatched. Deselect it, or dispatch its legs instead.";

const LOCATION_NOT_FOUND_SELECTED_REASON =
  "Your selection includes an order whose address couldn't be found. Fix the address, or deselect it.";

// Shared shape for a bulk action. `show` controls visibility for the current
// filter/selection; `disabled`/`disabledReason` gate execution. Both the
// CheckedShipmentsActions button bar and the BulkActionPalette render from this.
export interface BulkShipmentAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  show: boolean;
  disabled: boolean;
  disabledReason?: string;
  isProcessing?: boolean;
  color: BulkActionButtonColor;
  // Whether the button bar should render the label text next to the icon.
  showLabelOnButton?: boolean;
  // Optional keyboard shortcut letter that triggers this action while
  // shipments are checked (see BulkShipmentShortcutListener).
  shortcut?: string;
}

// Single source of truth for bulk shipment actions. Centralizes the visibility,
// disabled state, and click handlers so the button bar and the right-click
// palette stay in sync.
export function useBulkShipmentActions(): { actions: BulkShipmentAction[] } {
  const { rows, routingJobsData } = useShipmentsCoreDataContext();
  const { setModalOpen } = useModalsContext();
  const { selectedPrimaryFilter } = useShipmentFiltersContext();
  const { checkedShipments } = useShipmentSelectionContext();
  const { employeeId, hasPermission } = useAuth();
  const { isChangeDriversDisabled, changeDriversDisabledReason } =
    useBulkChangeDrivers();
  const {
    isDisabled: isBulkBolsDisabled,
    disabledReason: bulkBolsDisabledReason,
    isLoading: isBulkBolsLoading,
    handleClick: handleBulkBolsClick,
  } = useBulkPrintBOLs();

  const lastJob = routingJobsData?.routingJobs.edges?.[0]?.node;

  // Dispatch delay only applies to Created shipments, so we filter here before
  // feeding the hook. This keeps `remainingSeconds` 0 whenever no Created
  // shipment is selected, which is the contract `getOptimizeRoutesState`
  // expects for `remainingDispatchDelaySeconds`.
  const createdCheckedShipmentsData = checkedShipments
    .map((shipmentId) => rows.find((row) => row?.key === shipmentId))
    .filter((row) => row?.shipmentStatus === ShipmentStatus.Created)
    .map((row) => row?.shipment)
    .filter((shipment) => shipment !== undefined);

  const { remainingSeconds, formattedRemainingTime } =
    useDispatchDelayCountdown(createdCheckedShipmentsData);

  const { isDisabled: isOptimizeRoutesDisabled, reason: optimizeRoutesDisabledReason } =
    useMemo(() => {
      const selectedStatuses = checkedShipments
        .map((shipmentId) => rows.find((row) => row?.key === shipmentId))
        .filter((row): row is NonNullable<typeof row> => row !== undefined)
        .map((row) => row.shipmentStatus);

      return getOptimizeRoutesState({
        selectedStatuses,
        remainingDispatchDelaySeconds: remainingSeconds,
        formattedRemainingDispatchDelay: formattedRemainingTime,
      });
    }, [checkedShipments, rows, remainingSeconds, formattedRemainingTime]);

  // A billing-only order has no driving of its own — that lives on its legs — so
  // neither optimizing nor assigning can do anything with it. One in the selection
  // is enough to block both: the whole selection goes to the server together, and
  // the server refuses it. Better to say so up front than to let the dispatcher
  // press the button and get an error back.
  const hasNonDispatchableSelected: boolean = useMemo(
    () =>
      checkedShipments.some(
        (shipmentId) =>
          rows.find((row) => row?.key === shipmentId)?.shipment?.order
            ?.dispatchableType === DispatchableType.NonDispatchable
      ),
    [checkedShipments, rows]
  );

  // A stop Google Maps could not place sits at a 0,0 placeholder, so there is nowhere to
  // send a driver until someone fixes the address. Same reasoning as above: one in the
  // selection blocks both, because the whole selection goes to the server together.
  const hasLocationNotFoundSelected: boolean = useMemo(
    () =>
      checkedShipments.some((shipmentId) => {
        const row = rows.find((candidate) => candidate?.key === shipmentId);
        return Boolean(
          row?.origin?.isLocationNotFound || row?.destination?.isLocationNotFound
        );
      }),
    [checkedShipments, rows]
  );

  // A billing-only order never gets driven at all, so when an order is both that is the
  // more fundamental reason and the one worth showing.
  const blockedReason: string | undefined = hasNonDispatchableSelected
    ? NON_DISPATCHABLE_SELECTED_REASON
    : hasLocationNotFoundSelected
      ? LOCATION_NOT_FOUND_SELECTED_REASON
      : undefined;

  const isUnassignDriversDisabled = useMemo(
    () =>
      !checkedShipments.every((shipmentId) => {
        const status = rows.find(
          (row) => row?.key === shipmentId
        )?.shipmentStatus;
        return (
          status === ShipmentStatus.Assigned || status === ShipmentStatus.Routed
        );
      }),
    [checkedShipments, rows]
  );

  // Same permission the server checks on buildOrderGroups, so a dispatcher who
  // can't build groups never sees a button that would only fail.
  const canBuildOrderGroups: boolean = hasPermission("orderGroups.create");

  const handleOptimizeRoutesClick = () => {
    if (
      lastJob?.status === RoutingJobStatus.Pending ||
      lastJob?.status === RoutingJobStatus.Processing ||
      lastJob?.status === RoutingJobStatus.Ready
    ) {
      setModalOpen(true, OrdersPageModalNames.CREATE_ROUTING_JOB_WARNING);
      return;
    }
    setModalOpen(true, OrdersPageModalNames.CREATE_ROUTING_JOB);
  };

  const actions: BulkShipmentAction[] = useMemo(() => {
    const notClosedOrDelivered =
      selectedPrimaryFilter !== ShipmentsPrimaryFilterType.CLOSED &&
      selectedPrimaryFilter !== ShipmentsPrimaryFilterType.DELIVERED;
    const isActiveOrUnassigned =
      selectedPrimaryFilter === ShipmentsPrimaryFilterType.ACTIVE ||
      selectedPrimaryFilter === ShipmentsPrimaryFilterType.UNASSIGNED;

    return [
      {
        key: "optimizeRoutes",
        label: "Optimize Routes",
        icon: <SparklesIcon className="h-4 w-4" />,
        onClick: handleOptimizeRoutesClick,
        show: notClosedOrDelivered,
        disabled: isOptimizeRoutesDisabled || blockedReason !== undefined,
        disabledReason: blockedReason ?? (optimizeRoutesDisabledReason || undefined),
        color: "purple",
        showLabelOnButton: true,
        shortcut: "o",
      },
      {
        key: "assignDriver",
        label: "Assign Driver",
        icon: <IdentificationIcon className="h-4 w-4" />,
        onClick: () =>
          setModalOpen(true, OrdersPageModalNames.BULK_CHANGE_DRIVERS),
        show: notClosedOrDelivered,
        disabled: isChangeDriversDisabled || blockedReason !== undefined,
        disabledReason: blockedReason ?? (changeDriversDisabledReason || undefined),
        color: "secondary",
        shortcut: "a",
      },
      {
        key: "unassignDrivers",
        label: "Unassign Drivers",
        icon: <UserMinusIcon className="h-4 w-4" />,
        onClick: () =>
          setModalOpen(true, OrdersPageModalNames.BULK_UNASSIGN_DRIVERS),
        show: selectedPrimaryFilter !== ShipmentsPrimaryFilterType.UNASSIGNED,
        disabled: isUnassignDriversDisabled,
        color: "secondary",
        shortcut: "u",
      },
      {
        key: "changeHoldStatus",
        label: "Hold",
        icon: <HandRaisedIcon className="h-4 w-4" />,
        onClick: () =>
          setModalOpen(true, OrdersPageModalNames.BULK_CHANGE_HOLD_STATUS),
        show: notClosedOrDelivered,
        disabled: checkedShipments.length < 1,
        color: "secondary",
      },
      {
        key: "downloadPdfs",
        label: "Download Selected PDFs",
        icon: <ArrowDownTrayIcon className="h-4 w-4" />,
        onClick: () =>
          setModalOpen(true, OrdersPageModalNames.BULK_PRINT_SHIPPING_LABELS),
        show: checkedShipments.length > 0,
        disabled: false,
        color: "secondary",
        shortcut: "d",
      },
      {
        key: "downloadBols",
        label: "Download Selected BOLs",
        icon: <ArrowUpRightIcon className="h-4 w-4" />,
        onClick: handleBulkBolsClick,
        show: checkedShipments.length > 0,
        disabled: isBulkBolsDisabled,
        disabledReason: bulkBolsDisabledReason || undefined,
        isProcessing: isBulkBolsLoading,
        color: "secondary",
        shortcut: "b",
      },
      {
        key: "assignToRoute",
        label: "Assign to Route",
        icon: <Route className="h-4 w-4" />,
        onClick: () =>
          setModalOpen(true, OrdersPageModalNames.BULK_ASSIGN_TO_LIVE_ROUTE),
        show: isActiveOrUnassigned,
        disabled: checkedShipments.length < 1,
        color: "secondary",
        shortcut: "r",
      },
      {
        key: "editOrderLabels",
        label: "Edit Order Labels",
        icon: <TagIcon className="h-4 w-4" />,
        onClick: () =>
          setModalOpen(true, OrdersPageModalNames.BULK_ORDER_LABELS),
        show: true,
        disabled: checkedShipments.length === 0,
        color: "secondary",
        shortcut: "l",
      },
      {
        key: "setInvoiceDate",
        label: "Set Invoice Date",
        icon: <CalendarIcon className="h-4 w-4" />,
        onClick: () =>
          setModalOpen(true, OrdersPageModalNames.BULK_SET_INVOICE_DATE),
        show: true,
        disabled: checkedShipments.length === 0,
        color: "secondary",
      },
      {
        key: "buildOrderGroups",
        label: "Build Order Group",
        icon: <Group className="h-4 w-4" />,
        onClick: () =>
          setModalOpen(true, OrdersPageModalNames.BULK_BUILD_ORDER_GROUPS),
        show: canBuildOrderGroups,
        disabled: checkedShipments.length === 0,
        color: "secondary",
        shortcut: "g",
      },
      {
        key: "mergeOrders",
        label: "Merge Selected Orders",
        icon: <Merge className="h-4 w-4" />,
        onClick: () => setModalOpen(true, OrdersPageModalNames.BULK_MERGE_ORDERS),
        show: isActiveOrUnassigned,
        disabled: checkedShipments.length < 2,
        color: "red",
        shortcut: "m",
      },
      {
        key: "forceComplete",
        label: "Force Complete Selected Shipments",
        icon: <ClipboardDocumentCheckIcon className="h-4 w-4" />,
        onClick: () =>
          setModalOpen(true, OrdersPageModalNames.BULK_FORCE_COMPLETE_SHIPMENTS),
        show:
          notClosedOrDelivered &&
          selectedPrimaryFilter !== ShipmentsPrimaryFilterType.ALL,
        disabled: false,
        color: "red",
        shortcut: "f",
      },
      {
        key: "deleteSelected",
        label: "Delete Selected Shipments",
        icon: <TrashIcon className="h-4 w-4" />,
        onClick: () =>
          setModalOpen(true, OrdersPageModalNames.BULK_DELETE_SHIPMENTS),
        show: !!employeeId,
        disabled: false,
        color: "red",
      },
    ];
  }, [
    selectedPrimaryFilter,
    checkedShipments,
    employeeId,
    canBuildOrderGroups,
    blockedReason,
    isOptimizeRoutesDisabled,
    optimizeRoutesDisabledReason,
    isChangeDriversDisabled,
    changeDriversDisabledReason,
    isUnassignDriversDisabled,
    isBulkBolsDisabled,
    bulkBolsDisabledReason,
    isBulkBolsLoading,
    handleBulkBolsClick,
    lastJob,
  ]);

  return { actions };
}
